/**
 * `src/spa/registry.ts` — turn the app manifest's module list into single-spa registrations, backed
 * by `System.import()` against the injected import map (`src/app/`, D118).
 */
import { getMountedApps, registerApplication, unloadApplication, type AppProps, type LifeCycles } from "single-spa";
import type { ImportMapEntry, ResolvedMfe } from "@/app/types";
import { routeUnderPrefix } from "@/nav/navFilter";
import { MODULE_MAP_URL } from "@/app/moduleMap";

export function buildImportMap(entries: ImportMapEntry[]): { imports: Record<string, string> } {
  return { imports: Object.fromEntries(entries.map((e) => [e.key, e.url])) };
}

/** Dev-only: remembers `?mfe=` overrides for the rest of the tab's life. */
const DEV_MFE_OVERRIDES_KEY = "pinksy:dev:mfeOverrides";

/**
 * Dev-only: point one or more modules at a LOCAL build instead of the URL the app's import map
 * carries — `?mfe=@pinksy/people=<url of a local build>`, repeatable. The override is remembered per
 * tab so it survives a sign-in round trip, and it never runs on a real hostname: `boot.ts` gates the
 * call behind `isDevHost()`, because an import-map override on a live domain would be a
 * script-injection primitive. It is the last query-string override the shell has; `?tenantHost=`,
 * which chose which tenant a dev machine resolved as, went with D118.
 */
export function applyDevImportMapOverrides(
  entries: ImportMapEntry[],
  search: string = window.location.search,
): ImportMapEntry[] {
  const overrides = new Map<string, string>();
  try {
    const remembered = sessionStorage.getItem(DEV_MFE_OVERRIDES_KEY);
    for (const [key, url] of Object.entries(remembered ? (JSON.parse(remembered) as Record<string, string>) : {})) {
      overrides.set(key, url);
    }
  } catch {
    // best-effort — a remembered override just stops surviving the redirect
  }
  for (const raw of new URLSearchParams(search).getAll("mfe")) {
    const eq = raw.indexOf("=");
    if (eq > 0) overrides.set(raw.slice(0, eq), raw.slice(eq + 1));
  }
  if (!overrides.size) return entries;
  try {
    sessionStorage.setItem(DEV_MFE_OVERRIDES_KEY, JSON.stringify(Object.fromEntries(overrides)));
  } catch {
    /* see above */
  }
  return entries.map((e) => (overrides.has(e.key) ? { ...e, url: overrides.get(e.key) as string } : e));
}

/** Append the `<script type="systemjs-importmap">` SystemJS reads before any `System.import()` call. */
export function injectImportMap(entries: ImportMapEntry[], doc: Document = document): void {
  const script = doc.createElement("script");
  script.type = "systemjs-importmap";
  script.textContent = JSON.stringify(buildImportMap(entries));
  doc.head.appendChild(script);
}

type MfeMatch = Pick<ResolvedMfe, "pathPrefix" | "matchMode" | "overlay">;

/** Overlays (comms, help-center) mount on every route; everything else matches its prefix (or exact path). */
export function activeWhenFor(mfe: MfeMatch): (location: Pick<Location, "pathname">) => boolean {
  if (mfe.overlay) return () => true;
  if (mfe.matchMode === "exact") {
    return (location) => location.pathname === mfe.pathPrefix;
  }
  return (location) => routeUnderPrefix(location.pathname, mfe.pathPrefix);
}

/** `@pinksy/productions` → `Productions`, for a message a person can read. */
export function moduleLabel(importKey: string): string {
  const last = importKey.split("/").pop() ?? importKey;
  const spaced = last.replace(/[-_]+/g, " ").trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * Where to paint. Hosted modules mount straight onto `document.body`
 * (single-spa-vue's default target — App.vue's comment is the contract), so
 * there is no host container and no `domElement` prop to borrow. The fallback
 * therefore owns its own element, keyed by import key so a remount reuses it.
 */
function mountPointFor(props: AppProps, importKey: string): HTMLElement {
  const supplied = (props as { domElement?: unknown }).domElement;
  if (supplied instanceof HTMLElement) return supplied;
  const id = `pinksy-unavailable:${importKey}`;
  const existing = document.getElementById(id);
  if (existing) return existing;
  const host = document.createElement("div");
  host.id = id;
  host.className = "pinksy-module-unavailable-host";
  document.body.appendChild(host);
  return host;
}

/**
 * A module that will not load must still say so. Without this single-spa's
 * application simply dies in LOADING_SOURCE_CODE and the shell paints an empty
 * panel — the nav promises a screen and the product delivers a silent void,
 * which reads as "Pinksy is broken" rather than "this part is not built yet".
 * The reason is kept on screen (not only in the console) because the people who
 * hit this first are the three personas, not a developer with devtools open.
 */
export interface UnavailablePanel {
  bootstrap: (props: AppProps) => Promise<void>;
  mount: (props: AppProps) => Promise<void>;
  unmount: (props: AppProps) => Promise<void>;
}

/**
 * 🔴 **A MODULE THAT 404s IS USUALLY A TAB OLDER THAN THE LAST DEPLOY, AND THAT HAS A CURE.**
 *
 * `/modules/<name>/<version>/spa.js` is immutable and versioned, and the import map is read ONCE at
 * boot. So a tab open across a version bump keeps asking for the version it booted with — and if
 * that build is no longer on disk it 404s, single-spa dies in LOADING_SOURCE_CODE, and this panel
 * told the person "this part of Pinksy has not been built yet. Nothing you did caused this."
 *
 * True, unhelpful, and wrong about the cause: the part IS built, they are simply holding yesterday's
 * address for it. Devin hit this with a tab open across three bumps in an hour.
 *
 * 🔴 **AND THE ERROR TEXT CANNOT TELL THE TWO APART** — SystemJS says "Error loading …" for a module
 * that was never built and for one whose version has moved on, because both are a 404. So the panel
 * does not guess from a string: it ASKS, and compares the address the SERVER names now against the
 * one this tab tried. Different means the map moved under an open tab, which a reload fixes; the
 * same means the module really is not built, which a reload will not fix and must not be offered for.
 *
 * 🔴 **WHAT IT ASKS CHANGED WITH D118, AND IT HAD TO.** It used to re-fetch `GET /tenant-apps/resolve`
 * and read the import map out of the answer. The map is no longer served by the API — it is derived
 * at build time and baked into the bundle — and a tab asking its OWN bundle would only ever be told
 * what it already believes. So the build writes `/modules.json` beside the bundle (`vite.config.ts`),
 * served no-cache like `index.html`: one small file whose whole job is to say what the CURRENT deploy
 * calls each module. That is the same question the resolve endpoint was being asked, addressed to the
 * thing that actually knows the answer.
 */
async function movedOnSinceBoot(importKey: string, triedUrl: string | null): Promise<string | null> {
  if (triedUrl === null) return null;
  try {
    const answer = await fetch(MODULE_MAP_URL, { credentials: "omit", cache: "no-store" });
    if (!answer.ok) return null;
    const body = (await answer.json()) as { imports?: ImportMapEntry[] };
    const now = (body.imports ?? []).find((entry) => entry.key === importKey)?.url ?? null;
    return now !== null && now !== triedUrl ? now : null;
  } catch {
    // Offline, or the file is not there. Either way we cannot claim the map moved, so we do not.
    return null;
  }
}

/** The address SystemJS was reaching for, pulled back out of its own message. */
function urlFromCause(detail: string): string | null {
  return /(\/modules\/[^\s)"']+)/.exec(detail)?.[1] ?? null;
}

export function unavailableLifeCycles(importKey: string, cause: unknown): UnavailablePanel {
  const detail = cause instanceof Error ? cause.message : String(cause);
  let host: HTMLElement | null = null;
  return {
    bootstrap: async () => undefined,
    mount: async (props: AppProps) => {
      host = mountPointFor(props, importKey);
      const section = document.createElement("section");
      section.className = "pinksy-module-unavailable";
      section.setAttribute("role", "status");
      const heading = document.createElement("h2");
      heading.textContent = `${moduleLabel(importKey)} is not available yet`;
      const body = document.createElement("p");
      body.textContent =
        "This part of Pinksy has not been built yet, or its bundle failed to load. Nothing you did caused this.";
      const reason = document.createElement("p");
      reason.className = "pinksy-module-unavailable__reason";
      reason.textContent = detail;
      section.append(heading, body, reason);
      host.replaceChildren(section);

      // 🔴 Asked, not guessed — and asked AFTER painting, so a slow or dead resolve never leaves the
      // panel blank. If the server now names a different address for this module, this tab simply
      // booted before a version bump, and saying so turns a dead end into one click.
      void movedOnSinceBoot(importKey, urlFromCause(detail)).then((movedTo) => {
        if (movedTo === null || host === null || !host.contains(section)) return;
        heading.textContent = `${moduleLabel(importKey)} has moved on since this tab opened`;
        body.textContent =
          "A newer version of Pinksy was published while this tab was open, so it is asking for an address that no longer exists. Reloading picks up the new one — nothing you did caused this, and nothing is lost.";
        const reload = document.createElement("button");
        reload.type = "button";
        reload.className = "pinksy-module-unavailable__reload";
        reload.textContent = "Reload Pinksy";
        reload.addEventListener("click", () => window.location.reload());
        // The way out sits ABOVE the address: the person who hits this is a producer, not a developer.
        section.insertBefore(reload, reason);
      });
    },
    unmount: async () => {
      if (host?.classList.contains("pinksy-module-unavailable-host")) host.remove();
      else host?.replaceChildren();
      host = null;
    },
  };
}

/**
 * `System.import` (populated by the `systemjs` side-effect import in
 * `main.ts`) resolves to the module's single-spa lifecycle object directly —
 * it is NOT itself a further loading function, so this loader's signature
 * matches single-spa's `(config: AppProps) => Promise<LifeCycles>` contract.
 *
 * A failed import resolves to a panel that explains itself rather than
 * rejecting: a rejection kills the application and leaves the panel blank.
 */
function loadMfe(importKey: string): (config: AppProps) => Promise<LifeCycles> {
  return async () => {
    try {
      return await window.System.import<LifeCycles>(importKey);
    } catch (cause) {
      return unavailableLifeCycles(importKey, cause);
    }
  };
}

/**
 * Register every resolved module (and only those) as a single-spa application.
 *
 * `canMount` is the SESSION half of "is this application active". single-spa's own `activeWhen` is a
 * function of the path alone, so without this a sign-out changes no route, nothing unmounts, and the
 * next person at the same browser reads the last person's screen. The shell passes a gate that is
 * true while a session is live — or on a public path, where the claim link (P-09) is served
 * signed-out on purpose and must still mount.
 */
export function registerTenantModules(modules: ResolvedMfe[], canMount: () => boolean = () => true): void {
  for (const mfe of modules) {
    const matchesRoute = activeWhenFor(mfe);
    registerApplication({
      name: mfe.importKey,
      app: loadMfe(mfe.importKey),
      activeWhen: (location) => canMount() && matchesRoute(location),
    });
  }
}

/**
 * 🔴 **THROW AWAY WHAT EVERY MOUNTED MODULE IS HOLDING, AND LET IT ASK AGAIN.**
 *
 * Switching company changes a header, not an identity (`auth/http.ts`) — but every list already on
 * screen was fetched under the OLD one and is now another company's data still being drawn. The
 * Sidebar's own comment has always said so; what it did about it was dispatch a
 * `pinksy:organisation-changed` event that **no module listened to**, so the Desk went on showing
 * the previous company's productions under the new company's name. Reproduced in Chrome: switch
 * from a production company to an agency and the agency's rail draws while the production company's
 * show is still on the page.
 *
 * 🔴 **WHY UNLOAD RATHER THAN TELL THEM.** The obvious fix is an event each module listens for and
 * reacts to by refetching. That is ten modules, each with its own stores, and the failure mode of
 * missing one is silent: another company's data, under this company's name, on a platform whose
 * whole promise is that those are separate. A module that has been unloaded is holding nothing —
 * there is no list of stores to keep in step, and a module added next year is covered by
 * construction.
 *
 * single-spa re-mounts each one immediately, because the route still matches: what the person sees
 * is their screen reloading, not the page. `waitForUnmount: false` unmounts now rather than waiting
 * for a module that is wedged to agree.
 *
 * An app that is mid-mount or already broken throws here, and that must not stop the others being
 * cleared — a half-switched Desk is the state this exists to prevent.
 */
export async function reloadMountedModules(): Promise<void> {
  await Promise.all(
    getMountedApps().map(async (name) => {
      try {
        await unloadApplication(name, { waitForUnmount: false });
      } catch {
        // Nothing to do: it was not holding a mounted screen, which is the only thing at stake.
      }
    }),
  );
}
